import * as React from 'react';
import { useCallback } from 'react';
import { Controller } from 'react-hook-form';
import { TextInput as FlowBiteTextInput } from 'flowbite-react';

import { InputProps, borderColorObject, borderColorObjectType } from './form.types';
import { cn } from '@restworld/utils-common';

export type TextInputProps = InputProps & React.RefAttributes<HTMLInputElement>;
export const TextInput: React.FunctionComponent<TextInputProps> = React.forwardRef(
  (props: InputProps, ref?: React.Ref<HTMLInputElement>) => {
    const {
      onChange,
      rules = {},
      id,
      control,
      hasLeftPadding = false,
      borderless,
      twStyle,
      inputStyle,
      wrapperStyle,
      testId,
      isError,
      supportText,
      supportTextStyles,
      ...rest
    } = props;
    const handleChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        e.preventDefault();
        e.stopPropagation();
        if (onChange) onChange(e.target.value);
        return e;
      },
      [onChange]
    );

    const supportTextEl = React.useMemo(
      () =>
        supportText ? (
          <StyledSupportText $isError={isError} supportTextStyles={supportTextStyles}>
            {supportText}
          </StyledSupportText>
        ) : null,
      [isError, supportText, supportTextStyles]
    );

    if (control)
      return (
        <>
          <Controller
            name={id}
            control={control}
            rules={rules}
            render={({ field }) => (
              <TextInputStyled
                hasleftpadding={hasLeftPadding ? '1' : '0'}
                borderless={borderless ? '1' : '0'}
                twstyle={twStyle}
                wrapperstyle={wrapperStyle}
                inputstyle={inputStyle}
                data-cy={testId}
                $isError={isError}
                {...field}
                {...rest}
                ref={ref}
              />
            )}
          />
          {supportTextEl}
        </>
      );
    return (
      <>
        <TextInputStyled
          onChange={handleChange}
          hasleftpadding={hasLeftPadding ? '1' : '0'}
          borderless={borderless ? '1' : '0'}
          twstyle={twStyle}
          wrapperstyle={wrapperStyle}
          inputstyle={inputStyle}
          data-cy={testId}
          $isError={isError}
          {...rest}
          ref={ref}
        />
        {supportTextEl}
      </>
    );
  }
);

export default TextInput;
const prefixClasses = (prefix: string, classes: string) =>
  classes
    .split(/\s+/)
    .filter(Boolean)
    .map((cls) => `${prefix}${cls}`)
    .join(' ');

const TextInputStyled = ({
  borderless,
  bordercolor = 'primary' as borderColorObjectType,
  hasleftpadding,
  twstyle,
  wrapperstyle,
  inputstyle,
  $isError,
  className,
  ...props
}: any) => (
  <FlowBiteTextInput
    className={cn(
      'px-1',
      props.rightIcon && props.color === 'failure' && '[&_svg]:text-red-500',
      '[&_input]:p-3 [&_input]:border [&_input]:border-primary-200 [&_input]:rounded-lg [&_input]:focus:border-primary-300 [&_input]:focus:ring-primary-300 [&_input]:placeholder:!text-gray-400 [&_input]:placeholder:truncate [&_input]:placeholder:text-base [&_input]:placeholder:font-normal',
      props.color === 'failure' && '[&_input]:bg-red-50 [&_input]:border-red-500',
      borderless === '1' &&
        '[&_input]:border-0 [&_input]:bg-white [&_input]:focus-visible:border-0 [&_input]:focus-visible:ring-0 [&_input]:focus-visible:outline-0',
      borderColorObject[bordercolor] && prefixClasses('[&_input]:', borderColorObject[bordercolor]),
      hasleftpadding === '1' && '[&_input]:pl-10',
      props.rightIcon && '[&_input]:pr-10',
      $isError &&
        '[&_input]:!bg-red-50 [&_input]:placeholder:text-red-700 [&_input]:text-red-700 [&_input]:border-red-500 [&_input]:placeholder:!text-red-700',
      inputstyle && prefixClasses('[&_input]:', inputstyle),
      wrapperstyle,
      twstyle,
      className
    )}
    {...props}
  />
);
const StyledSupportText = ({ supportTextStyles, $isError, className, ...props }: any) => (
  <p
    className={cn('text-sm text-gray-500 font-normal leading-5.25 mt-3', $isError ? 'text-red-600' : '', className)}
    {...props}
  />
);

export type { InputProps };
